import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import { useCache } from '../contexts/CacheContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, RefreshCw, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { formatBookingDate, formatBookingTime } from '../utils/dateTimeUtils';
import toast from 'react-hot-toast';
import Spinner from '../components/Spinner';
import logger from '../utils/logger';

const Bookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalBookings, setTotalBookings] = useState(0);
  const [filter, setFilter] = useState('upcoming'); // default to upcoming view
  const [refreshing, setRefreshing] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger for manual refresh
  const { withCache, clearCache } = useCache();

  useEffect(() => {
    // Create AbortController for cleanup
    const abortController = new AbortController();

    // Prevent double calls on mount
    if (!hasInitialized) {
      setHasInitialized(true);
    }

    // Determine if this is a forced refresh
    const forceRefresh = refreshTrigger > 0;

    // Define async function inside useEffect
    const loadBookings = async () => {
logger.debug('BOOKINGS', 'LoadBookings called - forceRefresh:', forceRefresh, 'filter:', filter, 'page:', currentPage);

      // Reset error state
      setHasError(false);
    
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const params = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        upcoming_only: filter === 'upcoming' ? true : undefined,
        past_only: filter === 'past' ? true : undefined,
        sort_by: 'start_datetime',
        sort_order: 'desc'
      };
logger.debug('BOOKINGS', 'Calling API with params:', params);

      // Use cache for bookings data with filter-specific key
      const cacheKey = `bookings-${filter}-page${currentPage}`;

      let data;
      try {
        // Note: Signal parameter removed - API functions don't support it yet
        data = await withCache(
          cacheKey,
          () => userAPI.getBookings({ ...params }),
          forceRefresh
        );
logger.debug('BOOKINGS', 'Bookings data received successfully:', data);
      } catch (apiError) {
        // If the API call itself fails, re-throw to outer catch
logger.error('BOOKINGS', 'API call failed:', apiError);
        throw apiError;
      }
      
      // Handle paginated response format from backend
      let bookingsList = [];
      
      // Check for successful empty response
      if (data === null || data === undefined) {
        logger.debug('BOOKINGS', 'No bookings data returned');
        bookingsList = [];
        setTotalBookings(0);
      } else if (data && data.bookings !== undefined) {
        // Standard paginated format with bookings array (can be empty)
        bookingsList = Array.isArray(data.bookings) ? data.bookings : [];
        setTotalBookings(data.total || bookingsList.length);
      } else if (Array.isArray(data)) {
        // Legacy array format
        bookingsList = data;
        setTotalBookings(data.length);
      } else if (data && typeof data === 'object') {
        // Check if it's an empty response or has booking data in other format
        if (data.items && Array.isArray(data.items)) {
          bookingsList = data.items;
          setTotalBookings(data.total || data.items.length);
        } else if (Object.keys(data).length === 0) {
          // Empty object response
logger.debug('BOOKINGS', 'Empty bookings response');
          bookingsList = [];
          setTotalBookings(0);
        } else {
          // Try to extract bookings from other possible formats
logger.warn('BOOKINGS', 'Non-standard bookings format:', data);
          bookingsList = [];
          setTotalBookings(0);
        }
      } else {
logger.error('BOOKINGS', 'Unexpected bookings data format:', data);
        bookingsList = [];
        setTotalBookings(0);
      }
      
      // NOTE: Backend already filters by end_time for "upcoming" filter
      // No need for client-side filtering - trust the backend
      // The backend uses: end_time > now (includes active + future bookings)
      let filteredBookings = bookingsList;
      
      // Sort by date (newest first)
      filteredBookings.sort((a, b) => {
        const dateA = new Date(a.start_datetime || a.start_time || a.start_date || a.from);
        const dateB = new Date(b.start_datetime || b.start_time || b.start_date || b.from);
        return dateB - dateA; // DESC order (newest first)
      });
      
      setBookings(filteredBookings);
    } catch (error) {
      // Ignore AbortError - component was unmounted
      if (error.name === 'AbortError') {
logger.debug('BOOKINGS', 'Request aborted - component unmounted');
        return;
      }
logger.error('BOOKINGS', 'Error in loadBookings:', { error: error });

      // Set empty bookings regardless of error
      setBookings([]);
      setTotalBookings(0);

      // Only show toast for actual network/server errors
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network error. Please check your connection.');
      } else if (error.response) {
        // We got a response from server
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.detail || '';
logger.debug('BOOKINGS', 'Error response:', {
            status: status,
            message: message,
            data: error.response.data,
            url: error.config?.url,
            params: error.config?.params
          });

        // Don't show toast for "no data" type responses
        if (status === 404 && message.toLowerCase().includes('no bookings')) {
logger.debug('BOOKINGS', 'No bookings found - this is not an error');
          return; // Exit without showing error
        }

        // Only show error toast for real errors
        if (status >= 500) {
          // Server error - but we can still show empty state
logger.error('BOOKINGS', 'Server error loading bookings - showing empty state');
          setHasError(true);
          // Don't show error toast for now, just show empty bookings
          // toast.error('Server error. Please try again later.');
        } else if (status === 401) {
          toast.error('Session expired. Please log in again.');
        } else if (status === 403) {
          toast.error('You do not have permission to view bookings.');
        } else if (status !== 404) {
          // Don't show error for 404s as they might just mean no data
          toast.error('Unable to load bookings');
        }
      } else if (error.request) {
        // Request made but no response
        toast.error('No response from server. Please try again.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    };

    // Call loadBookings
    loadBookings();

    // Cleanup function - abort request on unmount
    return () => {
      abortController.abort();
    };
  }, [currentPage, filter, pageSize, hasInitialized, refreshTrigger, withCache]);

  const handleRefresh = () => {
    // Clear all booking-related caches
    clearCache(`bookings-all-page${currentPage}`);
    clearCache(`bookings-upcoming-page${currentPage}`);
    clearCache(`bookings-past-page${currentPage}`);

    // Trigger refresh by updating state
    setRefreshTrigger(prev => prev + 1);
    toast.success('Bookings refreshed');
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      confirmed: 'badge-success',
      pending: 'badge-warning',
      cancelled: 'badge-danger',
    };

    return (
      <span className={`badge ${statusStyles[status] || 'badge-secondary'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bookings-container">
        <div className="card">
          <Spinner size="md" centered text="Loading your bookings..." />
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-container">
      <div className="card">
        <div className="card-header">
          <div>
            <h1 className="card-title">
              <Calendar size={24} aria-hidden="true" />
              My Bookings
            </h1>
            <p className="text-secondary">View and manage your resource bookings</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate('/chat')}
              aria-label="Create new booking"
            >
              <Plus size={16} aria-hidden="true" />
              New Booking
            </button>
          </div>
        </div>
        
        <div className="filter-section">
          <div className="filter-buttons">
            <button
              onClick={() => { setFilter('upcoming'); setCurrentPage(1); }}
              className={`btn btn-sm ${filter === 'upcoming' ? 'btn-primary' : 'btn-outline'}`}
              aria-label="Show upcoming bookings"
              aria-pressed={filter === 'upcoming'}
            >
              Upcoming
            </button>
            <button
              onClick={handleRefresh}
              className="btn btn-sm btn-outline btn-refresh"
              disabled={refreshing}
              style={{ marginLeft: 'auto' }}
              aria-label="Refresh bookings list"
              aria-busy={refreshing}
            >
              <RefreshCw size={16} className={refreshing ? 'spinning' : ''} style={{ marginRight: '4px' }} aria-hidden="true" />
              Refresh
            </button>
          </div>
        </div>

        {bookings.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} />
            <h3>{hasError ? 'Unable to load bookings' : 'No bookings found'}</h3>
            <p>
              {hasError ? 
                'There was a problem loading your bookings. Please try again later.' :
                filter === 'upcoming' ? 'You have no upcoming bookings' : 
                filter === 'past' ? 'You have no past bookings' : 
                'You have not made any bookings yet'}
            </p>
            {hasError && (
              <button
                onClick={handleRefresh}
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                Try Again
              </button>
            )}
          </div>
        ) : (
          <>
          <div className="bookings-list">
            {bookings.map((booking) => (
              <div key={booking.id || booking.booking_id || Math.random()} className="booking-item">
                {booking.resource_image_url && (
                  <div className="booking-image">
                    <img 
                      src={booking.resource_image_url.startsWith('//') ? `https:${booking.resource_image_url}` : booking.resource_image_url}
                      alt={booking.resource_name || booking.space_name || booking.room_name || 'Resource'}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="booking-content">
                  <div className="booking-header">
                    <h4>
                      {(() => {
                        const resourceName = booking.resource_name ||
                                           booking.space_name ||
                                           booking.room_name ||
                                           booking.title ||
                                           booking.reference ||
                                           `Booking ${booking.id || booking.booking_id || ''}`;
                        const capacity = booking.resource_capacity;
                        return capacity ? `${resourceName} (Capacity: ${capacity})` : resourceName;
                      })()}
                    </h4>
                    {getStatusBadge(booking.status || booking.booking_status || 'confirmed')}
                  </div>

                <div className="booking-details">
                  {(booking.location || booking.location_name || booking.venue) && (
                    <div className="detail-item">
                      <MapPin size={16} />
                      <span>{booking.location || booking.location_name || booking.venue}</span>
                    </div>
                  )}

                  {(booking.start_datetime || booking.start_time || booking.start_date || booking.from) && (
                    <>
                      <div className="detail-item">
                        <Calendar size={16} />
                        <span>
                          {formatBookingDate(
                            booking.start_datetime || booking.start_time || booking.start_date || booking.from,
                            booking.timezone
                          )}
                        </span>
                      </div>

                      <div className="detail-item">
                        <Clock size={16} />
                        <span>
                          {formatBookingTime(
                            booking.start_datetime || booking.start_time || booking.start_date || booking.from,
                            booking.end_datetime || booking.end_time || booking.end_date || booking.to,
                            booking.timezone
                          )}
                        </span>
                      </div>
                    </>
                  )}

                  {(booking.attendees || booking.guests || booking.capacity) && (
                    <div className="detail-item">
                      <Users size={16} />
                      <span>{booking.attendees || booking.guests || booking.capacity} people</span>
                    </div>
                  )}

                  {/* Additional fields that might be present */}
                  {booking.description && (
                    <div className="detail-item">
                      <span className="booking-description">{booking.description}</span>
                    </div>
                  )}
                </div>

                  <div className="booking-footer">
                    <span className="booking-id">
                      {booking.reference ? 
                        `Ref: ${booking.reference}` : 
                        booking.confirmation_number ? 
                          `Confirmation: ${booking.confirmation_number}` :
                          `ID: ${booking.id || booking.booking_id || 'N/A'}`
                      }
                    </span>
                    <span className="booking-created">
                      Booked {format(new Date(booking.created_at || booking.created || booking.booked_at || new Date()), 'PP')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination Controls */}
          {totalBookings > pageSize && (
            <div className="pagination-container" role="navigation" aria-label="Bookings pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-outline"
                aria-label="Go to previous page"
              >
                Previous
              </button>
              <span className="pagination-info" aria-live="polite">
                Page {currentPage} of {Math.ceil(totalBookings / pageSize)} ({totalBookings} total bookings)
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalBookings / pageSize), prev + 1))}
                disabled={currentPage >= Math.ceil(totalBookings / pageSize)}
                className="btn btn-outline"
                aria-label="Go to next page"
              >
                Next
              </button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default Bookings;
